import React, { useState, useCallback } from 'react';
import { Box, Paper, Typography, TextField, Button, Alert } from '@mui/material';
import GuidedEntry from '../GuidedEntry';
import { postMeasurements, genEntryId } from './partsApi';
import { computeVerdict } from './partsFormat';

// ---------------------------------------------------------------------------
// PartMeasurementEntry — wraps the existing GuidedEntry with the part-feature
// measurement-ingest wiring (doc §5 Act 3 / §3.2 / §7).
//
// GuidedEntry itself is NOT modified here (out of this lane's file scope) —
// it has no native `verdictMode`. Instead this wrapper posts each value via
// apiFetch after GuidedEntry's onSubmitValue fires, computes the in/out-of-spec
// verdict client-side from the same usl/lsl GuidedEntry is already showing as
// spec chips, and flashes it in an overlay above GuidedEntry's own z-index —
// the inspector-vocabulary half of doc:330's "IN SPEC / OUT OF SPEC flash"
// (the *process alarm* half is Phase 2 — no alarm machinery exists yet).
//
// A fresh externalId (crypto-random UUID) is minted PER READING via
// genEntryId() at submit time — never once per session — so a timeout/retry
// of a single reading is idempotent (ON CONFLICT DO NOTHING) without risking
// two different readings deduping against each other.
//
// Required-context handling (doc §2.2 entry_config, §3.2 MISSING_CONTEXT):
//   - `requiredContext` (string[]) is the resolved required-field list for the
//     target stream (stream.meta._config.entry_config.required, falling back
//     to the part's entry_config_default) — resolved by the caller, since it
//     already has the part/feature/stream tree loaded. When non-empty, a small
//     pre-entry form collects those values ONCE before GuidedEntry mounts.
//   - Belt-and-suspenders: if the server still 400s a submit with
//     `MISSING_CONTEXT` (client's resolved list was stale/empty), the same
//     reading is held and the user is prompted inline for exactly the
//     server-reported missing keys, then the identical point (same
//     externalId) is retried — safe because ON CONFLICT DO NOTHING makes a
//     retry of an already-inserted row a no-op, and this row was never
//     inserted (it 400'd) so the retry is the first real insert attempt.
// ---------------------------------------------------------------------------

const inputSx = {
    '& .MuiOutlinedInput-root': {
        color: 'rgba(255, 255, 255, 0.9)',
        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
};

// Real entry_config field names (backend/measurementRoutes.js
// CONTEXT_FIELD_GETTERS) — the only four keys the server ever recognizes as
// required-context or reports in a MISSING_CONTEXT `details[].missing` list.
// An unrecognized key is always reported missing server-side (fail closed),
// so this map must stay in exact sync with CONTEXT_FIELD_GETTERS.
const FIELD_LABELS = {
    operator_label: 'Operator',
    gage_id: 'Gage ID',
    batch_label: 'Batch / lot label',
    machine: 'Machine',
};

// snake_case entry_config field name -> the wire (camelCase) point field the
// POST /api/features/:id/measurements body actually expects (doc §3.2; see
// backend/measurementRoutes.js parsePoint). gage_id is additionally coerced to
// an integer — the server 400s a non-integer gageId (parsePoint: "gageId must
// be a positive integer").
const CONTEXT_WIRE_MAP = {
    operator_label: 'operatorLabel',
    gage_id: 'gageId',
    batch_label: 'batchLabel',
    machine: 'machine',
};

function fieldLabel(key) {
    return FIELD_LABELS[key] || key;
}

/** Maps a snake_case {operator_label, gage_id, batch_label, machine} context
 * object (the pre-entry form's keys, matching entry_config field names) to the
 * camelCase wire fields postMeasurements sends — the ONE adapter point between
 * the entry_config vocabulary and the ingest body. */
function mapContextToWire(ctx) {
    const wire = {};
    for (const [key, rawValue] of Object.entries(ctx || {})) {
        if (rawValue === undefined || rawValue === null || rawValue === '') continue;
        const wireKey = CONTEXT_WIRE_MAP[key] || key;
        wire[wireKey] = key === 'gage_id' ? parseInt(rawValue, 10) : rawValue;
    }
    return wire;
}

function PreEntryContextForm({ requiredContext, onContinue }) {
    const [values, setValues] = useState({});
    const allFilled = requiredContext.every((k) => (values[k] || '').trim() !== '');

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, backgroundColor: '#0d1117' }}>
            <Paper sx={{ p: 3, maxWidth: 420, width: '100%', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
                    This stream requires a few details before recording
                </Typography>
                {requiredContext.map((key) => (
                    <TextField
                        key={key}
                        label={fieldLabel(key)}
                        size="small"
                        fullWidth
                        sx={inputSx}
                        type={key === 'gage_id' ? 'number' : 'text'}
                        value={values[key] || ''}
                        onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                        inputProps={key === 'gage_id' ? { min: 1, step: 1 } : { maxLength: 120 }}
                    />
                ))}
                <Button variant="contained" color="primary" disabled={!allFilled} onClick={() => onContinue(values)}>
                    Continue
                </Button>
            </Paper>
        </Box>
    );
}

function VerdictFlash({ verdict }) {
    if (!verdict) return null;
    const isIn = verdict === 'in';
    return (
        <Box
            sx={{
                position: 'fixed',
                top: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1400,
                px: 3,
                py: 1,
                borderRadius: 2,
                fontWeight: 700,
                letterSpacing: 0.5,
                fontSize: '0.9rem',
                backgroundColor: isIn ? 'rgba(129,199,132,0.95)' : 'rgba(229,115,115,0.95)',
                color: '#0d1117',
                boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
            }}
            data-testid="verdict-flash"
        >
            {isIn ? 'IN SPEC' : 'OUT OF SPEC'}
        </Box>
    );
}

function PartMeasurementEntry({
    part,
    feature,
    stream,
    requiredContext = [],
    onClose,
    onRecorded,
}) {
    const [contextValues, setContextValues] = useState(null); // null until collected (or [] required)
    const [values, setValues] = useState([]);
    const [verdictFlash, setVerdictFlash] = useState(null);
    const [pendingMissing, setPendingMissing] = useState(null); // { value, missing: [...] }
    const [error, setError] = useState(null);

    const specLimits = { usl: feature?.usl, lsl: feature?.lsl, target: feature?.nominal };
    const contextChips = [
        stream?.process_label ? { label: 'Process', value: stream.process_label } : null,
        feature?.unit ? { label: 'Unit', value: feature.unit } : null,
        ...(contextValues ? Object.entries(contextValues).map(([k, v]) => ({ label: fieldLabel(k), value: v })) : []),
    ].filter(Boolean);

    const contextLabel = part?.part_number
        ? `${part.part_number}${part.revision ? ` Rev ${part.revision}` : ''} — ${feature?.name || ''}`
        : (feature?.name || 'Guided Entry');

    const submitPoint = useCallback(async (value, ctx, retryExternalId) => {
        setError(null);
        const externalId = retryExternalId || genEntryId();
        // Wire fields: camelCase context (operatorLabel/gageId/batchLabel/machine,
        // gageId parsed to an integer) + source:'guided' — every reading recorded
        // through this GuidedEntry wrapper is a guided-entry point (doc §3.2
        // VALID_SOURCES), never the 'manual' default.
        const point = { value, externalId, source: 'guided', ...mapContextToWire(ctx) };
        try {
            const { ok, status, data } = await postMeasurements(feature.id, {
                points: [point],
                process: stream?.process_label,
            });
            if (!ok) {
                if (status === 400 && data?.code === 'MISSING_CONTEXT') {
                    // Real shape: {details: [{index, missing: [...]}]} — a per-point
                    // list, not a flat `missing` array (backend/measurementRoutes.js
                    // missingDetails; backend/tests/measurementRoutes.test.js
                    // "required-context validation" pins this). Flatten + de-dupe
                    // across every reported point; a single-point submit here means
                    // details has at most one entry, but flattening stays correct
                    // even if that ever changes.
                    const details = Array.isArray(data?.details) ? data.details : [];
                    const missing = [...new Set(details.flatMap((d) => d.missing || []))];
                    setPendingMissing({ value, externalId, missing: missing.length > 0 ? missing : requiredContext });
                    return;
                }
                setError(data?.error || 'Could not record that reading. It was not saved.');
                return;
            }
            setValues((prev) => [...prev, value]);
            const verdict = computeVerdict(value, specLimits);
            if (verdict) {
                setVerdictFlash(verdict);
                setTimeout(() => setVerdictFlash(null), 900);
            }
            onRecorded?.({ value, point: data?.points?.[0] });
        } catch (e) {
            setError('Network error — that reading was not saved. Try again.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feature, stream?.process_label, specLimits, requiredContext]);

    const handleSubmitValue = (value) => {
        submitPoint(value, contextValues || {});
    };

    const handleResolveMissing = (missingValues) => {
        const merged = { ...(contextValues || {}), ...missingValues };
        setContextValues(merged);
        const pending = pendingMissing;
        setPendingMissing(null);
        if (pending) {
            submitPoint(pending.value, merged, pending.externalId);
        }
    };

    // Pre-entry context collection: only for stream/parts that declare required
    // fields (doc §2.2 owner decision 4 — required context is config, not schema;
    // the baseline is all-optional).
    if (requiredContext.length > 0 && contextValues === null) {
        return <PreEntryContextForm requiredContext={requiredContext} onContinue={(v) => setContextValues(v)} />;
    }

    if (pendingMissing) {
        return (
            <PreEntryContextForm
                requiredContext={pendingMissing.missing}
                onContinue={(v) => handleResolveMissing(v)}
            />
        );
    }

    return (
        <>
            <VerdictFlash verdict={verdictFlash} />
            {error && (
                <Box sx={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 1400, width: '90%', maxWidth: 480 }}>
                    <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
                </Box>
            )}
            <GuidedEntry
                contextLabel={contextLabel}
                appendMode
                values={values}
                onSubmitValue={handleSubmitValue}
                onClose={onClose}
                backLabel="Back to Feature"
                submitLabel="Record (Enter)"
                specLimits={specLimits}
                contextChips={contextChips}
            />
        </>
    );
}

export default PartMeasurementEntry;
